import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import ConversationFeedback from "lib/api/ConversationFeedback";
import Company from "lib/api/Company";
import companyCache from "lib/utils/companyCache";
import { exportConversationFeedbackToCSV } from "lib/utils/csvExport";
import { InfiniteLoader, AutoSizer, Column, Table } from "react-virtualized";
import "react-virtualized/styles.css";
import PageLayout from "components/layouts/PageLayout";
import { Button, ButtonGroup, Dropdown, TextInput } from "components/common";
import { CompanyContext } from "lib/context/CompanyContext";

export default function ConversationFeedbackList(props) {
  const { company } = useContext(CompanyContext);
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recordsPerPage, setRecordsPerPage] = useState(100);
  const [conversationType, setConversationType] = useState("");
  const [feedbackType, setFeedbackType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [companyNameFilter, setCompanyNameFilter] = useState("");
  const [clientNameFilter, setClientNameFilter] = useState("");

  async function load() {
    await setLoading(true);
    try {
      let params = {};

      if (conversationType) {
        params.conversation_type = conversationType;
      }

      if (feedbackType) {
        params.feedback_type = feedbackType;
      }

      if (startDate) {
        params['gte~created_at'] = `${startDate}T00:00:00`;
      }

      if (endDate) {
        params['lte~created_at'] = `${endDate}T23:59:59`;
      }

      let response = await ConversationFeedback.get(0, recordsPerPage, params);
      switch (response.status) {
        case 200:
          const feedbackData = response.data;

          // If no data, clear records and set total to 0
          if (!feedbackData || feedbackData.length === 0) {
            setRecords([]);
            setTotal(0);
            break;
          }

          // Extract unique company_uuids
          const uniqueCompanyUuids = [...new Set(feedbackData.map(feedback => feedback.company_uuid))];
          const validCompanyUuids = uniqueCompanyUuids.filter(uuid => uuid);

          // Check cache first
          const cacheResult = companyCache.getMultiple(validCompanyUuids);
          const companyMap = { ...cacheResult.found };

          // Fetch missing companies in batches of 100
          if (cacheResult.missing.length > 0) {
            try {
              const batches = [];
              for (let i = 0; i < cacheResult.missing.length; i += 100) {
                batches.push(cacheResult.missing.slice(i, i + 100));
              }

              for (const batch of batches) {
                const companyPromises = batch.map(uuid =>
                  Company.load(uuid).catch(error => {
                    console.error(`Failed to fetch company ${uuid}:`, error);
                    return null;
                  })
                );

                const companies = await Promise.all(companyPromises);

                // Add to map and cache
                companies.forEach((comp, index) => {
                  if (comp) {
                    const uuid = batch[index];
                    const companyData = {
                      uuid: comp.uuid,
                      name: comp.name,
                      client_name: comp.client?.name || 'Unknown Client',
                      client_uuid: comp.client?.uuid
                    };
                    companyMap[uuid] = companyData;
                    companyCache.set(uuid, comp);
                  }
                });
              }
            } catch (error) {
              console.error('Failed to fetch company data:', error);
            }
          }

          // Enrich feedback data with company and client names
          const enrichedFeedback = feedbackData.map(feedback => ({
            ...feedback,
            company_name: companyMap[feedback.company_uuid]?.name || 'Unknown Company',
            client_name: companyMap[feedback.company_uuid]?.client_name || 'Unknown Client'
          }));

          // Just set allRecords - the useEffect will handle filtering
          setAllRecords(enrichedFeedback);
          break;
        case 401:
          props.history.push(`/`);
          break;
        default:
          console.log('An unhandled api error occurred.');
          console.log(response);
      }
    } catch (exception) {
      console.log(exception);
    }
    await setLoading(false);
  }

  function isRowLoaded({ index }) {
    return !!records[index];
  }

  async function loadMoreRows({ startIndex, stopIndex }) {
    try {
      if (loading) return;
      await setLoading(true);

      let params = {};

      if (conversationType) {
        params.conversation_type = conversationType;
      }

      if (feedbackType) {
        params.feedback_type = feedbackType;
      }

      if (startDate) {
        params['gte~created_at'] = `${startDate}T00:00:00`;
      }

      if (endDate) {
        params['lte~created_at'] = `${endDate}T23:59:59`;
      }

      let response = await ConversationFeedback.get(startIndex, Math.max(recordsPerPage, stopIndex - startIndex + 1), params);

      switch (response.status) {
        case 200:
          const feedbackData = response.data;

          // Extract unique company_uuids
          const uniqueCompanyUuids = [...new Set(feedbackData.map(feedback => feedback.company_uuid))];
          const validCompanyUuids = uniqueCompanyUuids.filter(uuid => uuid);

          // Check cache first
          const cacheResult = companyCache.getMultiple(validCompanyUuids);
          const companyMap = { ...cacheResult.found };

          // Fetch missing companies in batches of 100
          if (cacheResult.missing.length > 0) {
            try {
              const batches = [];
              for (let i = 0; i < cacheResult.missing.length; i += 100) {
                batches.push(cacheResult.missing.slice(i, i + 100));
              }

              for (const batch of batches) {
                const companyPromises = batch.map(uuid =>
                  Company.load(uuid).catch(error => {
                    console.error(`Failed to fetch company ${uuid}:`, error);
                    return null;
                  })
                );

                const companies = await Promise.all(companyPromises);

                // Add to map and cache
                companies.forEach((comp, index) => {
                  if (comp) {
                    const uuid = batch[index];
                    const companyData = {
                      uuid: comp.uuid,
                      name: comp.name,
                      client_name: comp.client?.name || 'Unknown Client',
                      client_uuid: comp.client?.uuid
                    };
                    companyMap[uuid] = companyData;
                    companyCache.set(uuid, comp);
                  }
                });
              }
            } catch (error) {
              console.error('Failed to fetch company data:', error);
            }
          }

          // Enrich feedback data with company and client names
          const enrichedFeedback = feedbackData.map(feedback => ({
            ...feedback,
            company_name: companyMap[feedback.company_uuid]?.name || 'Unknown Company',
            client_name: companyMap[feedback.company_uuid]?.client_name || 'Unknown Client'
          }));

          // Update allRecords with new data - the useEffect will handle filtering
          setAllRecords(prevRecords => [...prevRecords, ...enrichedFeedback]);
          break;
        case 401:
          props.history.push(`/`);
          break;
        default:
          console.log('An unhandled api error occurred.');
          console.log(response);
      }
    } catch (exception) {
      console.log(exception);
    }
    await setLoading(false);
  }

  useEffect(() => {
    load();
    return () => { setLoading(false) };
  }, [company, conversationType, feedbackType, startDate, endDate]);

  // Handle company name and client name filter changes (client-side filtering)
  useEffect(() => {
    let filteredRecords = allRecords;

    // Apply company name filter
    if (companyNameFilter) {
      filteredRecords = filteredRecords.filter(record =>
        record.company_name.toLowerCase().includes(companyNameFilter.toLowerCase())
      );
    }

    // Apply client name filter
    if (clientNameFilter) {
      filteredRecords = filteredRecords.filter(record =>
        record.client_name.toLowerCase().includes(clientNameFilter.toLowerCase())
      );
    }

    setRecords(filteredRecords);
    setTotal(filteredRecords.length);
  }, [companyNameFilter, clientNameFilter, allRecords]);

  const formatFeedbackType = (type) => {
    if (!type) return "";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatConversationType = (type) => {
    if (!type) return "";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getFeedbackTypeColor = (type) => {
    switch (type) {
      case 'positive':
        return '#10b981'; // green
      case 'negative':
        return '#ef4444'; // red
      case 'neutral':
        return '#6b7280'; // gray
      default:
        return '#000000';
    }
  };

  const handleExport = async () => {
    if (records.length === 0) {
      alert('No data to export');
      return;
    }

    // Export current records
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    exportConversationFeedbackToCSV(records, `conversation_feedback_${timestamp}.csv`);
  };

  return (
    <PageLayout>
      <h1>Dela Conversations</h1>
      <div className="hr hr--darkest"></div>
      <div className="row">
        <div className="col-xs-12 col-sm-2">
          <TextInput
            label="Client Name"
            value={clientNameFilter}
            onChange={e => setClientNameFilter(e.target.value)}
            placeholder="Filter by client..."
          />
        </div>
        <div className="col-xs-12 col-sm-2">
          <TextInput
            label="Company Name"
            value={companyNameFilter}
            onChange={e => setCompanyNameFilter(e.target.value)}
            placeholder="Filter by company..."
          />
        </div>
        <div className="col-xs-12 col-sm-1">
          <Dropdown
            label="Type"
            value={conversationType}
            onChange={e => setConversationType(e.target.value)}
          >
            <option value="">All</option>
            <option value="voice">Voice</option>
            <option value="text">Text</option>
          </Dropdown>
        </div>
        <div className="col-xs-12 col-sm-1">
          <Dropdown
            label="Feedback"
            value={feedbackType}
            onChange={e => setFeedbackType(e.target.value)}
          >
            <option value="">All</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
            <option value="neutral">Neutral</option>
          </Dropdown>
        </div>
        <div className="col-xs-12 col-sm-2">
          <label>
            <span className="label__span">Start Date</span>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </label>
        </div>
        <div className="col-xs-12 col-sm-2">
          <label>
            <span className="label__span">End Date</span>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </label>
        </div>
        <div className="col-xs-12 col-sm-2">
          <label style={{ visibility: 'hidden', marginBottom: 0 }}>
            <span className="label__span">Export</span>
          </label>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={records.length === 0}>
              Export
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {total === 0 && !loading && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          No conversation feedback found matching the selected filters.
        </div>
      )}

      {total > 0 && (
        <InfiniteLoader
        isRowLoaded={isRowLoaded}
        loadMoreRows={loadMoreRows}
        rowCount={total}
      >
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer>
            {({ height, width }) => {
              return (
                <Table
                  headerHeight={40}
                  width={width}
                  height={height - 120}
                  rowCount={total}
                  rowHeight={40}
                  disableHeader={false}
                  rowGetter={({ index }) => records[index] || {}}
                  onRowsRendered={onRowsRendered}
                  ref={registerChild}
                >
                  <Column
                    label="Client"
                    dataKey="client_name"
                    width={width * 0.17}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return "Loading....";
                      }
                      return cellData;
                    }}
                  />
                  <Column
                    label="Company"
                    dataKey="company_name"
                    width={width * 0.15}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return "Loading....";
                      }
                      return cellData;
                    }}
                  />
                  <Column
                    label="Type"
                    dataKey="conversation_type"
                    width={width * 0.06}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return "Loading....";
                      }
                      return formatConversationType(cellData);
                    }}
                  />
                  <Column
                    label="Feedback"
                    dataKey="feedback_type"
                    width={width * 0.06}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return "Loading....";
                      }
                      return (
                        <span style={{ color: getFeedbackTypeColor(cellData) }}>
                          {formatFeedbackType(cellData)}
                        </span>
                      );
                    }}
                  />
                  <Column
                    label="Platform"
                    dataKey="conversation_data"
                    width={width * 0.12}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData || !cellData.platform) {
                        return "N/A";
                      }
                      return cellData.platform;
                    }}
                  />
                  <Column
                    label="Messages"
                    dataKey="conversation_data"
                    width={width * 0.08}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData || !cellData.conversation_messages) {
                        return "0";
                      }
                      return cellData.conversation_messages.length;
                    }}
                  />
                  <Column
                    label="Duration"
                    dataKey="conversation_data"
                    width={width * 0.07}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData || !cellData.session_duration_seconds) {
                        return "N/A";
                      }
                      return `${cellData.session_duration_seconds}s`;
                    }}
                  />
                  <Column
                    label="Record Created"
                    dataKey="conversation_data"
                    width={width * 0.09}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData || cellData.successfully_created_record === undefined) {
                        return "N/A";
                      }
                      return cellData.successfully_created_record ? "Yes" : "No";
                    }}
                  />
                  <Column
                    label="Created At"
                    dataKey="created_at"
                    width={width * 0.12}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return "N/A";
                      }
                      return new Date(cellData).toLocaleString();
                    }}
                  />
                  <Column
                    label=""
                    dataKey="uuid"
                    width={width * 0.12}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return "Loading...";
                      }
                      return (
                        <ButtonGroup>
                          <Link className="button m-bot-0" to={`/conversation-feedback/${encodeURIComponent(cellData)}`}>
                            View
                          </Link>
                        </ButtonGroup>
                      );
                    }}
                  />
                </Table>
              );
            }}
          </AutoSizer>
        )}
      </InfiniteLoader>
      )}
    </PageLayout>
  );
}
